import React from 'react';
import { motion } from 'framer-motion';

// Tech stack item component
const TechItem = ({ name, logo, filter = true, useTextFallback = false, size = "normal" }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="flex items-center justify-between bg-gradient-to-br from-purple-900/30 to-indigo-900/30 
               rounded-xl backdrop-blur-sm border border-purple-500/20 
               hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10 
               transition-all duration-300 p-5 h-20"
  >
    <span className="text-white font-medium text-lg">{name}</span>
    <div className="w-12 h-12 flex items-center justify-center">
      {useTextFallback ? (
        <div className="w-10 h-10 rounded-full bg-purple-500/50 flex items-center justify-center text-white font-bold">
          {name.charAt(0)}
        </div>
      ) : name === "MistralAI" ? (
        <img 
          src={logo} 
          alt={name} 
          className="w-10 h-10 md:w-12 md:h-12 object-contain" 
        />
      ) : name === "Vercel" ? (
        <img 
          src={logo} 
          alt={name} 
          className="w-10 h-10 md:w-12 md:h-12 object-contain" 
        />
      ) : (
        <img 
          src={logo} 
          alt={name} 
          className={`max-h-10 max-w-10 object-contain ${filter ? 'filter invert-[0.15] brightness-150' : ''}`} 
        />
      )}
    </div>
  </motion.div>
)

const Services = () => {
  // Tech stack data
  const techStack = {
    webDevelopment: [
      { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" },
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg" },
      { name: "Express.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg" },
      { 
        name: "Vercel", 
        logo: "/vercel-icon-dark.svg",
        filter: false,
        size: "large"
      },
    ],
    mobileDevelopment: [
      { name: "React Native", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
    ],
    databasesAndApi: [
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" },
      { 
        name: "REST API", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/a/ab/Swagger-logo.png",
        filter: false 
      },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" },
      { 
        name: "Stripe", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg",
        filter: false 
      },
      { name: "Supabase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg" },
      { 
        name: "Make.com", 
        logo: "/Make-Icon-Circle-Purple.svg",
        filter: false 
      },
      { 
        name: "OpenAI", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg",
        filter: false 
      },
      { 
        name: "MistralAI", 
        logo: "/Mistral_AI.svg",
        filter: false,
        size: "large"
      },
      { 
        name: "Claude by Anthropic", 
        logo: "/claude-icon.svg",
        filter: false
      },
      { 
        name: "Twilio", 
        logo: "/twilio-icon.svg",
        filter: false
      },
      { 
        name: "Retell AI", 
        logo: "/Retell-black.svg",
        filter: false,
        size: "large"
      },
    ],
  }

  return (
    <div className="relative min-h-screen flex items-center py-16 md:py-24">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:48px_48px]" />
      
      {/* Glowing Orb Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-[128px] -z-10 animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/30 rounded-full blur-[128px] -z-10 animate-pulse delay-700" />

      <div className="container mx-auto px-4 space-y-12 md:space-y-16">
        {/* Tech Stack Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            Tech Stack
          </h2>
          <p className="text-gray-300 text-center mb-12 max-w-3xl mx-auto">
            These are the tools we use to reach the optimal software solution.
          </p>

          <div className="space-y-24">
            {/* Web Development */}
            <div>
              <div className="flex items-center mb-8">
                <div className="h-0.5 bg-gradient-to-r from-purple-500 to-transparent flex-grow"></div>
                <h3 className="text-2xl font-semibold text-white px-6 relative">
                  Web Development
                </h3>
                <div className="h-0.5 bg-gradient-to-l from-purple-500 to-transparent flex-grow"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {techStack.webDevelopment.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TechItem 
                      name={tech.name} 
                      logo={tech.logo} 
                      filter={tech.filter} 
                      useTextFallback={tech.useTextFallback || false} 
                      size={tech.size || "normal"}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Mobile Development */}
            <div>
              <div className="flex items-center mb-8">
                <div className="h-0.5 bg-gradient-to-r from-purple-500 to-transparent flex-grow"></div>
                <h3 className="text-2xl font-semibold text-white px-6 relative">
                  Mobile Development
                </h3>
                <div className="h-0.5 bg-gradient-to-l from-purple-500 to-transparent flex-grow"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {techStack.mobileDevelopment.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TechItem 
                      name={tech.name} 
                      logo={tech.logo} 
                      filter={tech.filter} 
                      useTextFallback={tech.useTextFallback || false} 
                      size={tech.size || "normal"}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Databases & API Integration */}
            <div>
              <div className="flex items-center mb-8">
                <div className="h-0.5 bg-gradient-to-r from-purple-500 to-transparent flex-grow"></div>
                <h3 className="text-2xl font-semibold text-white px-6 relative">
                  Databases & API Integration
                </h3>
                <div className="h-0.5 bg-gradient-to-l from-purple-500 to-transparent flex-grow"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {techStack.databasesAndApi.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TechItem 
                      name={tech.name} 
                      logo={tech.logo} 
                      filter={tech.filter} 
                      useTextFallback={tech.useTextFallback || false} 
                      size={tech.size || "normal"}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;